import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown, AlertTriangle, PiggyBank } from "lucide-react";

interface FinancialInsightsProps {
  period: "month" | "quarter" | "year";
  startDate: string;
  endDate: string;
}

interface MonthlyTotal {
  income: number;
  expenses: number;
  balance: number;
}

interface Transaction {
  type: string;
  amount: number;
  payment_status: string;
  created_at: string;
}

type MonthData = {
  [key: string]: MonthlyTotal;
};

type MonthEntry = [string, MonthlyTotal];

export function FinancialInsights({ period, startDate, endDate }: FinancialInsightsProps) {
  const { data: insights, isLoading } = useQuery({
    queryKey: ["financial-insights", period, startDate, endDate],
    queryFn: async () => {
      const { data: transactions, error } = await supabase
        .from("transactions")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Calculate monthly totals
      const monthlyTotals = (transactions as Transaction[]).reduce((acc: MonthData, transaction) => {
        const month = new Date(transaction.created_at).toISOString().slice(0, 7);
        if (!acc[month]) {
          acc[month] = { income: 0, expenses: 0, balance: 0 };
        }
        if (transaction.type === "entrada") {
          acc[month].income += Number(transaction.amount);
        } else {
          acc[month].expenses += Number(transaction.amount);
        }
        acc[month].balance = acc[month].income - acc[month].expenses;
        return acc;
      }, {});

      // Find most profitable month
      const monthEntries = Object.entries(monthlyTotals) as MonthEntry[];
      const defaultMonth: MonthEntry = ["", { income: 0, expenses: 0, balance: 0 }];
      
      const mostProfitableMonth = monthEntries.length > 0 
        ? monthEntries.reduce<MonthEntry>((max, current) => 
            current[1].balance > max[1].balance ? current : max
          , monthEntries[0])
        : defaultMonth;

      // Find month with highest expenses
      const highestExpenseMonth = monthEntries.length > 0
        ? monthEntries.reduce<MonthEntry>((max, current) => 
            current[1].expenses > max[1].expenses ? current : max
          , monthEntries[0])
        : defaultMonth;

      // Calculate pending payments
      const pendingPayments = (transactions as Transaction[]).filter(
        (t) => t.payment_status === "pendente"
      ).length;

      // Calculate suggested reserve (3 months of average expenses)
      const totalExpenses = monthEntries.reduce(
        (sum, [, month]) => sum + month.expenses,
        0
      );
      const averageMonthlyExpenses = monthEntries.length > 0 
        ? totalExpenses / monthEntries.length 
        : 0;

      return {
        mostProfitableMonth,
        highestExpenseMonth,
        pendingPayments,
        suggestedReserve: averageMonthlyExpenses * 3,
      };
    },
  });

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatMonth = (monthStr: string) => {
    if (!monthStr) return "Nenhum dado disponível";
    const [year, month] = monthStr.split("-");
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString(
      "pt-BR",
      { month: "long", year: "numeric" }
    );
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white">Insights Financeiros</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-[#222831] border-[#404040]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">Mês Mais Lucrativo</p>
              <h4 className="text-lg font-semibold text-green-500">
                {formatMonth(insights?.mostProfitableMonth[0] || "")}
              </h4>
              <p className="text-sm text-white">
                {formatCurrency(insights?.mostProfitableMonth[1]?.balance || 0)}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-500" />
          </div>
        </Card>

        <Card className="p-4 bg-[#222831] border-[#404040]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">Maior Despesa</p>
              <h4 className="text-lg font-semibold text-red-500">
                {formatMonth(insights?.highestExpenseMonth[0] || "")}
              </h4>
              <p className="text-sm text-white">
                {formatCurrency(insights?.highestExpenseMonth[1]?.expenses || 0)}
              </p>
            </div>
            <TrendingDown className="h-8 w-8 text-red-500" />
          </div>
        </Card>

        <Card className="p-4 bg-[#222831] border-[#404040]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">Pagamentos Pendentes</p>
              <h4 className="text-lg font-semibold text-yellow-500">
                {insights?.pendingPayments || 0}
              </h4>
              <p className="text-sm text-white">faturas</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-yellow-500" />
          </div>
        </Card>

        <Card className="p-4 bg-[#222831] border-[#404040]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-400">Reserva Sugerida</p>
              <h4 className="text-lg font-semibold text-blue-500">
                {formatCurrency(insights?.suggestedReserve || 0)}
              </h4>
              <p className="text-sm text-white">3 meses de despesas</p>
            </div>
            <PiggyBank className="h-8 w-8 text-blue-500" />
          </div>
        </Card>
      </div>
    </div>
  );
}